// PRD 05 §3.6 — patient-app preview modal. iPhone-frame dialog that walks
// the user through the questions of the currently-selected item, starting at
// the question whose `📱 미리보기` button they clicked.
//
// Each answer type has a dedicated mobile renderer; selection is
// presentational only (this is a preview, not a live response form). The
// 묶음 응답형 (BATTERY) preview drives its own ◀▶ pagination inside the
// progress bar.
//
// Close: ✕ button, overlay click, Escape.

import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, ChevronLeft, ChevronRight, Image } from 'lucide-react';

import type { Choice, Question } from '../types/questionnaire-design.types';

// Flatten parent + children in document order so the "next" / "complete"
// button walks through the same sequence the author sees.
function flattenForPreview(questions: Question[]): Question[] {
  const tops = questions.filter((q) => !q.parentQid).sort((a, b) => a.order - b.order);
  const out: Question[] = [];
  for (const top of tops) {
    out.push(top);
    const subs = questions
      .filter((q) => q.parentQid === top.qid)
      .sort((a, b) => a.order - b.order);
    out.push(...subs);
  }
  return out;
}

interface MobilePreviewModalProps {
  open: boolean;
  // Questions in the editor's current scope; preview walks through this
  // ordered list starting at `startQid`.
  questions: Question[];
  startQid: string | null;
  onClose: () => void;
}

export function MobilePreviewModal({
  open,
  questions,
  startQid,
  onClose,
}: MobilePreviewModalProps) {
  const { t } = useTranslation('questionnaire-design');
  const flat = flattenForPreview(questions);
  const startIdx = Math.max(
    0,
    flat.findIndex((q) => q.qid === startQid),
  );
  const [curIdx, setCurIdx] = useState(startIdx);
  // BATTERY questions render as one outer card per question; the inner items
  // get their own ◀▶ navigation that updates the progress bar too.
  const [batteryItemIdx, setBatteryItemIdx] = useState(0);

  // Reset whenever the modal re-opens or the start question changes.
  useEffect(() => {
    if (open) {
      setCurIdx(startIdx);
      setBatteryItemIdx(0);
    }
  }, [open, startIdx]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || flat.length === 0) return null;

  const q = flat[curIdx];
  if (!q) return null;

  const isBattery = q.answerType === 'BATTERY';
  const batteryItems = isBattery
    ? (q.batteryConfig?.categories ?? []).flatMap((c) =>
        c.items.map((it) => ({ ...it, categoryName: c.name })),
      )
    : [];

  const batteryTotal = batteryItems.length;
  const totalForProgress = isBattery && batteryTotal > 0 ? batteryTotal : flat.length;
  const numForProgress = isBattery && batteryTotal > 0 ? batteryItemIdx + 1 : curIdx + 1;
  const pct = totalForProgress > 0 ? Math.round((numForProgress / totalForProgress) * 100) : 0;
  const isLast = curIdx >= flat.length - 1;

  const goNext = () => {
    if (isLast) {
      onClose();
      return;
    }
    setCurIdx((i) => i + 1);
    setBatteryItemIdx(0);
  };

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('preview.ariaLabel')}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.65)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t('preview.closeAria')}
        style={{
          position: 'absolute',
          top: 18,
          right: 18,
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: '#fff',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#374151',
          fontSize: 24,
          fontWeight: 700,
          boxShadow: '0 2px 8px rgba(0,0,0,.25)',
          zIndex: 10010,
        }}
      >
        ✕
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 375,
          height: 720,
          background: '#fff',
          borderRadius: 44,
          border: '3px solid #1a1a1a',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,.35)',
          position: 'relative',
        }}
      >
        {/* Notch */}
        <div
          style={{
            width: 120,
            height: 28,
            background: '#000',
            borderRadius: '0 0 18px 18px',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
          }}
        />
        {/* Status bar */}
        <div
          style={{
            height: 48,
            background: '#000',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            padding: '0 28px 6px',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          <span>9:41</span>
          <span style={{ fontSize: 11 }}>🔋</span>
        </div>
        {/* Appbar */}
        <div
          style={{
            height: 56,
            background: 'linear-gradient(135deg,#3B82F6,#2563EB)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
            color: '#fff',
            fontSize: 17,
            fontWeight: 700,
            flexShrink: 0,
            letterSpacing: '-.2px',
          }}
        >
          <span style={{ marginRight: 10 }}>💚</span>
          {t('preview.appbarName')}
        </div>
        {/* Progress */}
        <div
          style={{
            padding: '10px 20px 6px',
            background: '#f8f9fb',
            borderBottom: '1px solid #eee',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: '#6b7280',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>
              {t('preview.progress', {
                current: numForProgress,
                total: totalForProgress,
              })}
            </span>
            <span>{pct}%</span>
          </div>
          <div
            style={{
              height: 4,
              background: '#e5e7eb',
              borderRadius: 2,
              overflow: 'hidden',
              marginTop: 4,
            }}
          >
            <div
              style={{
                height: '100%',
                background: '#3B82F6',
                borderRadius: 2,
                transition: 'width .3s',
                width: `${pct}%`,
              }}
            />
          </div>
        </div>
        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 20,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {isBattery ? (
            <BatteryPreview
              question={q}
              itemIdx={batteryItemIdx}
              setItemIdx={setBatteryItemIdx}
              items={batteryItems}
            />
          ) : (
            <>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.65,
                  color: '#111827',
                  marginBottom: 6,
                }}
              >
                {q.text}
                {q.allowPhoto ? (
                  <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 6 }}>
                    {t('preview.photoAvailable')}
                  </span>
                ) : null}
              </div>
              <AnswerPreview key={q.qid} question={q} />
              {q.allowPhoto ? <PhotoAttachmentSection /> : null}
            </>
          )}
        </div>
        {/* Bottom */}
        <div
          style={{
            padding: '12px 20px 28px',
            flexShrink: 0,
            background: '#fff',
            borderTop: '1px solid #f3f4f6',
          }}
        >
          <button
            type="button"
            onClick={goNext}
            style={{
              width: '100%',
              height: 48,
              borderRadius: 14,
              background: '#3B82F6',
              color: '#fff',
              border: 'none',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {isLast
              ? t('preview.complete')
              : t('preview.next')}
          </button>
        </div>
        {/* Home indicator */}
        <div
          style={{
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: '#fff',
          }}
        >
          <div
            style={{ width: 134, height: 5, background: '#1a1a1a', borderRadius: 3 }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Per-type renderers ───────────────────────────────────────────────────

function PillRadio({
  text,
  score,
  selected,
  onSelect,
}: {
  text: string;
  score: number;
  selected: boolean;
  onSelect?: () => void;
}) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 16px',
        border: `1.5px solid ${selected ? '#3B82F6' : '#e5e7eb'}`,
        borderRadius: 14,
        fontSize: 15,
        color: '#374151',
        background: selected ? '#EFF6FF' : '#fff',
        minHeight: 48,
        cursor: onSelect ? 'pointer' : 'default',
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          border: `2px solid ${selected ? '#3B82F6' : '#d1d5db'}`,
          background: selected ? '#3B82F6' : 'transparent',
          boxShadow: selected ? 'inset 0 0 0 3px #fff' : 'none',
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1 }}>{text}</span>
      <span style={{ fontSize: 12, color: '#9ca3af' }}>
        {score}
        {t('answerDisplay.scoreSuffix')}
      </span>
    </div>
  );
}

function PillCheckbox({
  text,
  score,
  selected,
  onSelect,
}: {
  text: string;
  score: number;
  selected: boolean;
  onSelect?: () => void;
}) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 16px',
        border: `1.5px solid ${selected ? '#3B82F6' : '#e5e7eb'}`,
        borderRadius: 14,
        fontSize: 15,
        color: '#374151',
        background: selected ? '#EFF6FF' : '#fff',
        minHeight: 48,
        cursor: onSelect ? 'pointer' : 'default',
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 5,
          border: `2px solid ${selected ? '#3B82F6' : '#d1d5db'}`,
          background: selected ? '#3B82F6' : 'transparent',
          boxShadow: selected ? 'inset 0 0 0 2px #fff' : 'none',
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1 }}>{text}</span>
      <span style={{ fontSize: 12, color: '#9ca3af' }}>
        {score}
        {t('answerDisplay.scoreSuffix')}
      </span>
    </div>
  );
}

// PRD 05 §3.3 — an "기타(Other)" choice carries a free-text field the patient
// fills in. In the preview it is revealed only once the Other option is
// selected, and is typeable, matching the real answer flow (SHCT-421 #3).
function OtherTextInput() {
  const { t } = useTranslation('questionnaire-design');
  return (
    <input
      type="text"
      placeholder={t('preview.otherTextPlaceholder')}
      style={{
        border: '1.5px solid #3B82F6',
        borderRadius: 12,
        padding: '12px 14px',
        fontSize: 14,
        color: '#111',
        background: '#fff',
        width: '100%',
      }}
    />
  );
}

// 단답형 — single-select. Selecting the 기타 option reveals its text input.
function SingleChoicePreview({ choices }: { choices: Choice[] }) {
  const { t } = useTranslation('questionnaire-design');
  const [selected, setSelected] = useState<number | null>(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
      {choices.map((c, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <PillRadio
            text={c.chType === 'other' ? t('preview.otherChoice') : c.text}
            score={c.score}
            selected={selected === i}
            onSelect={() => setSelected(i)}
          />
          {c.chType === 'other' && selected === i ? <OtherTextInput /> : null}
        </div>
      ))}
    </div>
  );
}

// 중복선택형 — multi-select. Checking the 기타 option reveals its text input.
function MultiChoicePreview({ choices }: { choices: Choice[] }) {
  const { t } = useTranslation('questionnaire-design');
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const toggle = (i: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
      {choices.map((c, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <PillCheckbox
            text={c.chType === 'other' ? t('preview.otherChoice') : c.text}
            score={c.score}
            selected={selected.has(i)}
            onSelect={() => toggle(i)}
          />
          {c.chType === 'other' && selected.has(i) ? <OtherTextInput /> : null}
        </div>
      ))}
    </div>
  );
}

function AnswerPreview({ question }: { question: Question }) {
  const { t } = useTranslation('questionnaire-design');
  switch (question.answerType) {
    case 'SHORT_ANSWER':
      return <SingleChoicePreview choices={question.choices ?? []} />;
    case 'MULTI_CHOICE':
      return <MultiChoicePreview choices={question.choices ?? []} />;
    case 'YES_NO':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {(question.choices ?? []).map((c, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 64,
                borderRadius: 16,
                fontSize: 18,
                fontWeight: 600,
                border: '2px solid #e5e7eb',
                background: i === 0 ? '#EFF6FF' : '#fff',
                color: i === 0 ? '#1D4ED8' : '#374151',
              }}
            >
              {c.text}
            </div>
          ))}
        </div>
      );
    case 'OX':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {(question.choices ?? []).map((c, i) => {
            const isO = c.text === 'O';
            return (
              <div
                key={i}
                style={{
                  width: '100%',
                  aspectRatio: 1,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 36,
                  fontWeight: 700,
                  border: `3px solid ${isO ? '#93C5FD' : '#FCA5A5'}`,
                  background: isO ? '#EFF6FF' : '#FEF2F2',
                  color: isO ? '#2563EB' : '#DC2626',
                  maxWidth: 120,
                  margin: '0 auto',
                }}
              >
                {c.text}
              </div>
            );
          })}
        </div>
      );
    case 'TEXT': {
      const cfg = question.textConfig;
      return (
        <>
          <textarea
            disabled
            placeholder={t('preview.textPlaceholder')}
            style={{
              border: '1.5px solid #e5e7eb',
              borderRadius: 12,
              padding: '14px 16px',
              fontSize: 15,
              color: '#111',
              background: '#f9fafb',
              width: '100%',
              minHeight: 80,
              resize: 'none',
              fontFamily: 'inherit',
              marginBottom: 6,
            }}
          />
          <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
            {t('preview.textRange', {
              min: cfg?.minChars ?? 1,
              max: cfg?.maxChars ?? 500,
            })}
          </div>
        </>
      );
    }
    case 'SLIDER': {
      const cfg = question.sliderConfig ?? { min: 0, max: 10, step: 1, unit: '점' };
      const mid = Math.round((cfg.min + cfg.max) / 2);
      return (
        <div style={{ padding: '8px 0 16px' }}>
          <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, color: '#3B82F6', marginBottom: 4 }}>
            {t('preview.sliderValue', {
              value: mid,
              unit: cfg.unit,
            })}
          </div>
          <div
            style={{
              position: 'relative',
              height: 6,
              background: '#e5e7eb',
              borderRadius: 3,
              margin: '24px 0 8px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: '50%',
                background: '#3B82F6',
                borderRadius: 3,
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: -9,
                left: '50%',
                width: 24,
                height: 24,
                background: '#3B82F6',
                borderRadius: '50%',
                border: '3px solid #fff',
                boxShadow: '0 2px 6px rgba(0,0,0,.2)',
                transform: 'translateX(-50%)',
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280' }}>
            <span>{cfg.min}</span>
            <span>{cfg.max}</span>
          </div>
        </div>
      );
    }
    case 'PHOTO': {
      const cfg = question.photoConfig ?? { maxFiles: 1, maxSizeMB: 5 };
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            padding: '20px 0',
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: '#EFF6FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#3B82F6',
              fontSize: 28,
            }}
          >
            <Camera size={28} />
          </div>
          <button
            type="button"
            disabled
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 500,
              background: '#3B82F6',
              color: '#fff',
              border: '1.5px solid #3B82F6',
              cursor: 'default',
              textAlign: 'center',
            }}
          >
            <Camera size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{' '}
            {t('preview.photoCapture')}
          </button>
          <button
            type="button"
            disabled
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 500,
              background: '#fff',
              color: '#374151',
              border: '1.5px solid #e5e7eb',
              cursor: 'default',
              textAlign: 'center',
            }}
          >
            <Image size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{' '}
            {t('preview.photoGallery')}
          </button>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
            {t('preview.photoMax', {
              files: cfg.maxFiles,
              size: cfg.maxSizeMB,
            })}
          </div>
        </div>
      );
    }
    default:
      return (
        <div
          style={{
            padding: 20,
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 14,
          }}
        >
          {t('preview.unsupported')}
        </div>
      );
  }
}

function PhotoAttachmentSection() {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #e5e7eb' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
        {t('preview.photoAttachTitle')}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 12,
            border: '2px dashed #d1d5db',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            fontSize: 10,
          }}
        >
          <span style={{ fontSize: 22, marginBottom: 2 }}>+</span>
          {t('preview.photoAdd')}
        </div>
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 12,
            background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            fontSize: 11,
          }}
        >
          {t('preview.photoSample')}
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
        {t('preview.photoHint')}
      </div>
    </div>
  );
}

// BATTERY type has its own card with category header + question text + scale
// chips + ◀▶ navigation through items.
function BatteryPreview({
  question,
  itemIdx,
  setItemIdx,
  items,
}: {
  question: Question;
  itemIdx: number;
  setItemIdx: (idx: number | ((prev: number) => number)) => void;
  items: { text: string; allowPhoto: boolean; categoryName: string }[];
}) {
  const { t } = useTranslation('questionnaire-design');
  const cfg = question.batteryConfig;
  if (!cfg || items.length === 0) {
    return (
      <div
        style={{
          padding: 20,
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 14,
        }}
      >
        {t('preview.batteryEmpty')}
      </div>
    );
  }
  const item = items[itemIdx];
  return (
    <div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '.5px',
          marginBottom: 12,
          padding: '6px 12px',
          background: '#f3f4f6',
          borderRadius: 8,
        }}
      >
        {item.categoryName}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1.6,
          color: '#111827',
          textAlign: 'center',
          padding: '16px 8px',
          minHeight: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.text}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        {cfg.scaleLabels.map((label, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '10px 4px',
              borderRadius: 12,
              textAlign: 'center',
              fontSize: 11,
              lineHeight: 1.3,
              border: '1.5px solid #e5e7eb',
              color: '#374151',
              background: '#fff',
            }}
          >
            {label}
          </div>
        ))}
      </div>
      {item.allowPhoto ? (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #e5e7eb' }}>
          <div style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
            <Camera size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{' '}
            {t('preview.photoAttachTitle')}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 10,
                border: '2px dashed #d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9ca3af',
                fontSize: 18,
              }}
            >
              +
            </div>
          </div>
        </div>
      ) : null}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 16,
          paddingTop: 12,
          borderTop: '1px solid #f3f4f6',
        }}
      >
        <button
          type="button"
          onClick={() => setItemIdx((p) => Math.max(0, p - 1))}
          disabled={itemIdx === 0}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: '1.5px solid #e5e7eb',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: itemIdx === 0 ? 'default' : 'pointer',
            color: '#6b7280',
            opacity: itemIdx === 0 ? 0.4 : 1,
          }}
          aria-label={t('preview.prevAria')}
        >
          <ChevronLeft size={16} />
        </button>
        <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
          {itemIdx + 1} / {items.length}
        </div>
        <button
          type="button"
          onClick={() => setItemIdx((p) => Math.min(items.length - 1, p + 1))}
          disabled={itemIdx === items.length - 1}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: '1.5px solid #e5e7eb',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: itemIdx === items.length - 1 ? 'default' : 'pointer',
            color: '#6b7280',
            opacity: itemIdx === items.length - 1 ? 0.4 : 1,
          }}
          aria-label={t('preview.nextAria')}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

